"use client";

import {
  Avatar,
  Card,
  LiquidPill,
} from "components/ui/v2";
import {
  GripVertical,
  MessageCircle,
  Share2,
  ThumbsUp,
  Users,
} from "lucide-react";
import Image from "next/legacy/image";
import useTranslation from "next-translate/useTranslation";
import RateSkills from "components/_Pages/Profile/RateSkills";
import type { IUserEndorsementResult } from "types/Endorsement.type";
import type { ISkill } from "types/Skills.type";
import {
  getSkillLevelBadgeVariant,
  getSkillLevelLabel,
  getSkillLevelStyling,
} from "./skill-level-utils";

export interface EndorsementSkillCardV2Props {
  item: IUserEndorsementResult;
  index: number;
  isOwnProfile: boolean;
  canReorder: boolean;
  canShare: boolean;
  ratingDisabled?: boolean;
  onReview: (skill: ISkill) => void;
  onValidate?: (skillId: string, skillName: string) => void;
  onShare?: (skill: ISkill) => void;
  onRatingChange?: (rating: number, skill: ISkill) => void;
}

export function EndorsementSkillCardV2({
  item,
  index,
  isOwnProfile,
  canReorder,
  canShare,
  ratingDisabled = false,
  onReview,
  onValidate,
  onShare,
  onRatingChange,
}: EndorsementSkillCardV2Props) {
  const { t } = useTranslation("common");
  const level = item.skill.average || 0;
  const styling = getSkillLevelStyling(level);

  return (
    <Card size="full" padding="comfortable" shadow="subtle" entranceAnimation>
      <div className={`absolute left-0 right-0 top-0 h-1 ${styling.statusBar}`} />

      <div className="text-left">
        <div className="flex items-start gap-3">
          {canReorder ? (
            <div className="mt-0.5 shrink-0 cursor-grab text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300">
              <GripVertical className="h-4 w-4" />
            </div>
          ) : null}

          <div className="mt-0.5 shrink-0">
            <div className="relative">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
                <Image
                  src={index < 2 ? `/icon/skill-${index + 1}.svg` : "/icon/skill-3.svg"}
                  width={28}
                  height={28}
                  alt="skill"
                />
              </div>
              {index < 3 ? (
                <div
                  className={`absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white ${
                    index === 0 ? "bg-yellow-500" : index === 1 ? "bg-gray-400" : "bg-orange-500"
                  }`}
                >
                  {index + 1}
                </div>
              ) : null}
            </div>
          </div>

          <div className="min-w-0 flex-1 text-left">
            <h3 className="line-clamp-2 text-left text-base font-semibold leading-tight text-slate-900 dark:text-white">
              {item.skill.name}
            </h3>
            <div className="mt-0.5 flex items-center gap-2 text-left text-sm text-slate-500 dark:text-zinc-400">
              <Users className="h-4 w-4 shrink-0" />
              <span>
                <span className="font-semibold text-slate-900 dark:text-white">{item.times}</span>{" "}
                {t("Validaciones")}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <LiquidPill
              as="button"
              type="button"
              variant="slate"
              appearance="tinted"
              shadow="neutral"
              size="sm"
              shape="circle"
              icon={<MessageCircle className="h-4 w-4" />}
              aria-label={t("Ver validaciones")}
              onClick={() => onReview(item.skill)}
            />

            {canShare && onShare ? (
              <LiquidPill
                as="button"
                type="button"
                variant="sky"
                appearance="tinted"
                shadow="neutral"
                size="sm"
                shape="circle"
                icon={<Share2 className="h-4 w-4" />}
                aria-label={t("Compartir habilidad")}
                onClick={() => onShare(item.skill)}
              />
            ) : null}

            {!isOwnProfile && onValidate ? (
              <LiquidPill
                as="button"
                type="button"
                variant="sky"
                appearance="tinted"
                shadow="neutral"
                size="sm"
                shape="circle"
                icon={<ThumbsUp className="h-4 w-4" />}
                aria-label={t("Validar habilidad")}
                onClick={() => onValidate(item.skill.id, item.skill.name || "")}
              />
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <LiquidPill
            as="span"
            variant={getSkillLevelBadgeVariant(level)}
            appearance="tinted"
            size="xs"
            text={getSkillLevelLabel(level)}
          />

          {isOwnProfile && onRatingChange ? (
            <div className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-zinc-800">
              <RateSkills
                defaultValue={level}
                disabled={ratingDisabled}
                onChange={(rating) => onRatingChange(rating, item.skill)}
              />
            </div>
          ) : null}
        </div>

        {item.userEndorsements?.length > 0 ? (
          <div className="mt-4 space-y-2 border-t border-slate-200/70 pt-3 text-left dark:border-zinc-800">
            <p className="text-left text-xs font-medium text-slate-600 dark:text-zinc-400">
              {t("Validado por")}:
            </p>
            <div className="flex items-center gap-1">
              <div className="flex -space-x-2">
                {item.userEndorsements.slice(0, 8).map((endorsement) => (
                  <div
                    key={endorsement.endorserUser.id}
                    className="rounded-full ring-2 ring-white dark:ring-zinc-900"
                    title={`${endorsement.endorserUser.name} ${endorsement.endorserUser.lastname || ""}`}
                  >
                    <Avatar
                      src={endorsement.endorserUser.profileImage}
                      name={`${endorsement.endorserUser.name} ${endorsement.endorserUser.lastname || ""}`}
                      size="sm"
                    />
                  </div>
                ))}
                {item.userEndorsements.length > 8 ? (
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600 ring-2 ring-white dark:bg-zinc-700 dark:text-zinc-300 dark:ring-zinc-900">
                    +{item.userEndorsements.length - 8}
                  </div>
                ) : null}
              </div>
              <LiquidPill
                as="button"
                type="button"
                variant="sky"
                appearance="tinted"
                size="xs"
                text={t("Ver todas")}
                onClick={() => onReview(item.skill)}
              />
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
